// ============================================
// Marketing funnel configuration
//
// Single source of truth for the Phase A -> Phase B CTA flip
// (G2M plan: docs/2026-07-12-g2m-plan-v2.md, Section 4).
//
// DAY-0 FLIP: when the app is approved on the App Store, set
// APP_STORE_URL to the real store link. Every marketing page,
// badge, and hero CTA flips from email capture to App Store
// download off this one constant. Nothing else to touch.
//
// FLIPPED 2026-08-12. PRTCL is live on the App Store as
// "PRTCL - Your 1st Peptide Guide". Setting APP_STORE_URL turns every
// badge, sticky CTA, nav link, capture-success line, and web-lockdown
// notice into a real download CTA. Pre-approval branches are kept so
// the switch stays reversible; they no longer render.
// ============================================

import { Capacitor } from '@capacitor/core';

export const APP_STORE_URL: string | null = 'https://apps.apple.com/us/app/prtcl-your-1st-peptide-guide/id6762325414';

// Pre-approval fallback destination for App Store badges
// (mirrors LandingPage's APP_STORE_FALLBACK). Unused while
// APP_STORE_URL is set; kept for reversibility.
export const APP_STORE_FALLBACK = '/beta-onboarding';

export const SITE_ORIGIN = 'https://www.prtclapp.com';

export const appStoreLive = (): boolean => APP_STORE_URL !== null;

// ============================================
// WEB = MARKETING / LEAD-GEN ONLY (2026-07-15)
//
// Founder rule (verbatim): "There should be no way to sign into the web
// app anywhere at all. Anything we do for the G2M shall collect emails,
// show guides or let users download guides. But never should we have any
// sign in buttons or sign in routes that work from the web. iOS is all
// we're showing and allowing users to use."
//
// This is the ONE switch every lockdown behavior flows from. Native iOS
// keeps the full authenticated app; web browsers (prtclapp.com desktop +
// mobile web, and the build-time headless-Chrome prerender) get none of
// it - no sign in, no sign up, no dashboard/protocols/calculator/etc.
//
// To fully restore web auth in one move: set WEB_AUTH_ENABLED = true.
// Nothing else to touch - routers, nav, /learn, share pages, and the
// beta walkthrough all read isProductSurfaceAllowed().
// ============================================
export const WEB_AUTH_ENABLED = false;

/**
 * True when the authenticated product surface (sign in, sign up, and every
 * route that renders the logged-in app) may render or be entered.
 *
 * - Native iOS/Android shell: always true (full app, untouched).
 * - Web browsers incl. the prerender headless Chrome: only when
 *   WEB_AUTH_ENABLED is true.
 *
 * Keyed off Capacitor.isNativePlatform() so native behavior can never be
 * affected by the web lockdown.
 */
export function isProductSurfaceAllowed(): boolean {
  return Capacitor.isNativePlatform() || WEB_AUTH_ENABLED;
}

// ---- Analytics hygiene ----
// The build prerenders these pages in headless Chrome, which would fire
// page-view events into prod app_events on every deploy. navigator.webdriver
// is true for puppeteer/playwright and honest bots; skip tracking for them.
export function isAutomatedBrowser(): boolean {
  try {
    return navigator.webdriver === true;
  } catch {
    return false;
  }
}

// ---- Traffic source attribution (waitlist + analytics) ----
//
// Reads ?src= (preferred, used on partner QR cards) or ?utm_source=
// from the URL once and keeps it for the session, so a visitor who
// lands on /reconstitution?src=partner-insert and signs up two pages
// later still attributes to the partner funnel.
const SRC_KEY = 'prtcl-traffic-src';

export function captureTrafficSource(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const src = params.get('src') || params.get('utm_source');
    if (src && !sessionStorage.getItem(SRC_KEY)) {
      sessionStorage.setItem(SRC_KEY, src.slice(0, 64));
    }
  } catch {
    // sessionStorage unavailable (private mode edge cases) - attribution is best-effort
  }
}

export function getTrafficSource(pageSlug: string): string {
  try {
    const stored = sessionStorage.getItem(SRC_KEY);
    return stored ? `${pageSlug}:${stored}` : pageSlug;
  } catch {
    return pageSlug;
  }
}

// Raw stored source (no page prefix). Used by the partner context banner
// to detect visitors who arrived from a "partner..." QR / insert card.
// Client-only: returns null during prerender (sessionStorage empty), so
// callers must read it inside an effect and render the banner post-mount.
export function getRawTrafficSource(): string | null {
  try {
    return sessionStorage.getItem(SRC_KEY);
  } catch {
    return null;
  }
}
